/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { GraphSignature } from '../GraphSignature';
import { signersOfFileByStage } from './signersOfFileByStage';
import { Button, Chip, MoreButton } from '@design-system';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { EditSignerModal } from '@pages/DocumentPage/components/EditSignerModal';
import { IDocument, IInvite, IInviteStatus, ISigner } from '@cincel/client';


const links = (canDelete, canModify) => [
  ...(canModify
    ? [
      {
        label: translations.COMMON.EDIT,
        value: 'edit',
      },
    ]
    : [{}]),
  ...(canDelete
    ? [
      {
        label: translations.COMMON.DELETE,
        value: 'delete',
      },
    ]
    : []),
];

export interface SignersList {
  signers: IInvite[];
  handleSign: (type: string) => void;
  document: IDocument;
  currentInvitation?: IInvite;
  validateKYC: () => void;
  isKycInProgress: boolean;
  isBlocked: boolean;
  isLoading: boolean;
  isAutographDisabled: boolean;
  hasPermissions: boolean;
  currentSigner?: ISigner;
  sendReminder: (id: string) => void;
  primaryColor: string;
  actionsEnabled: boolean;
  isObserver: boolean;
  handleDeleteSigner: (id: string) => void;
  handleEditSigner: (id: IInvite) => void;
  isReminderLoading: boolean;
  userVerified: boolean;

}

// No need for separate TypedSignature component - autograph and name are treated the same

export const SignersList: React.FC<SignersList> = ({
  signers = [],
  handleSign,
  document,
  currentInvitation,
  currentSigner,
  validateKYC,
  isKycInProgress,
  isBlocked,
  isAutographDisabled = true,
  hasPermissions = false,
  isObserver = false,
  sendReminder,
  primaryColor,
  actionsEnabled,
  handleDeleteSigner,
  handleEditSigner,
  isReminderLoading,
  userVerified = false,
}) => {
  const { t } = useTranslation();
  const [modal, toggleModal] = useState(false);
  const [editSigner, setEditSigner] = useState();
  const [stageSigners, setStageSigners] = useState<any>([]);
  const [activeStage, setActiveStage] = useState<any>(null);
  const [isUserAvailable, setIsUserAvailable] = useState<any>(false);
  const [userReminderLoading, setUserReminderLoading] = useState();

  const handleValidateKYC = () => {
    validateKYC();
  };

  const handleSendReminder = async (id) => {
    try {
      setUserReminderLoading(id);
      sendReminder(id);
    } catch (err) {
      console.error(err as Error);
    }
  };

  useEffect(() => {
    if (signers && document) {
      const newSigners = signersOfFileByStage(signers);
      setStageSigners(newSigners);

      let unfinishedStage = null;
      newSigners.forEach((stage) => {
        if (unfinishedStage === null) {
          stage.forEach((s) => {
            if (!s.has_sign) {
              unfinishedStage = s.stage;
            }
          });
        }
      });
      setActiveStage(unfinishedStage);
    }
  }, [signers, document, currentInvitation, isObserver, hasPermissions]);

  useEffect(() => {
    if (activeStage === null || typeof activeStage === 'undefined') {
      // everyone signed
      setIsUserAvailable(false);
    } else if (
      currentInvitation?.stage &&
      activeStage >= currentInvitation.stage &&
      !isBlocked &&
      currentInvitation?.status !== IInviteStatus.COMPLETED
    ) {
      // invitation is from an active stage
      setIsUserAvailable(true);
    }

    if (isBlocked || currentInvitation?.status === IInviteStatus.COMPLETED) {
      setIsUserAvailable(false);
    }
  }, [stageSigners, activeStage, currentSigner, currentInvitation, isBlocked]);

  useEffect(() => {
    return () => {
      setActiveStage(null);
    };
  }, []);

  const onClickMore = (value, uuid) => {
    if (value === 'delete') {
      handleDeleteSigner(uuid);
    }
    if (value === 'edit') {
      const signerToEdit = signers.findIndex((s) => s.uuid === uuid);

      if (signerToEdit > -1) {
        setEditSigner({
          uuid,
          invitationId: uuid,
          name: signers[signerToEdit].name,
          email: signers[signerToEdit].email,
        } as any);
        toggleModal(true);
      }
    }
  };

  const handleShow = () => {
    toggleModal((prev) => !prev);
  };

  const handleEditSuccess = (signer) => {
    handleEditSigner(signer);
    setEditSigner(undefined);
    toggleModal(false);
  };

  const cleanSigner = (signer) => {
    setEditSigner(signer);
  };

  return (
    <div>
      {stageSigners.map((stage, index) => {
        return (
          <div key={index} className="mb-2 border-b ">
            <p className="mb-2 text-left text-paragraph font-bold text-gray-500">
              {t(translations.COMMON.STAGE)} {index + 1}
            </p>
            <div className="rounded-2xl border-2 border-gray-100 bg-basic-white p-3 md:px-4 md:py-5">
              {stage.map((signer, index) => {
                const canDelete =
                  stage.length > 1 && document?.status === 'unsigned';
                const canModify = true;

                // Parse signature types - autograph and name (typed) are treated the same
                const signatureTypes =
                  signer.signature_allowed_types?.split(',') || [];
                const hasAutograph = signatureTypes.includes('autograph');
                const hasAdvanced = signatureTypes.includes('advanced');
                const hasTyped = signatureTypes.includes('name'); // Backend sends "name" for typed

                // Group autograph and typed together since they use the same UI
                const hasAutographOrTyped = hasAutograph || hasTyped;

                const totalTypes = [hasAutographOrTyped, hasAdvanced].filter(
                  Boolean,
                ).length;
                const hasMultipleTypes = totalTypes > 1;

                if (
                  currentSigner?.email === signer.email &&
                  currentSigner?.email === currentInvitation?.email
                ) {
                  return (
                    <div key={signer.id}>
                      <div className="mb-2 flex">
                        <div className="mr-2 flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0 mr-2">
                              <p className="text-sm font-semibold truncate">
                                {signer.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {signer.email}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                              {signer.has_sign ? (
                                <Chip color={'#30C668'}>
                                  {t(translations.COMMON.SIGNED)}
                                </Chip>
                              ) : (
                                <>
                                  <Chip color={'#F02F60'}>
                                    {t(translations.COMMON.NO_SIGN)}
                                  </Chip>
                                  {isUserAvailable && userVerified && (
                                    <Button
                                      className="mt-2 px-8 text-white"
                                      size="sm"
                                      isDisabled={isAutographDisabled}
                                      onClick={() => handleSign(hasAdvanced ? 'advanced' : 'autograph')}
                                      color={primaryColor}
                                      variant="solid">
                                      {t(translations.COMMON.SIGN)}
                                    </Button>
                                  )}
                                </>
                              )}
                              {isUserAvailable &&
                                currentInvitation?.identity_verification &&
                                !userVerified && (
                                  <Button
                                    className="mt-2 px-8 text-white"
                                    size="sm"
                                    onClick={() => handleValidateKYC()}
                                    color={primaryColor}
                                    isLoading={isKycInProgress}
                                    variant="solid">
                                    {t(translations.COMMON.VALIDATE)}
                                  </Button>
                                )}
                            </div>
                          </div>
                        </div>
                        <div className="ml-auto flex h-[20px]">
                          {actionsEnabled && !signer.has_sign && (
                            <MoreButton
                              className="relative top-2 ml-auto"
                              items={
                                links(canDelete, canModify) as Array<{
                                  value: string;
                                  label: string;
                                }>
                              }
                              onClick={(value) =>
                                onClickMore(value, signer.idInvitation)
                              }
                            />
                          )}
                        </div>
                      </div>
                      {index !== stage.length - 1 && (
                        <hr className="mb-4  border border-dashed border-gray-100 md:min-w-full" />
                      )}
                    </div>
                  );
                }

                return (
                  <div key={signer.id} className="mb-4 flex flex-col">
                    <div className="flex">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0 mr-2">
                            <p className="text-sm font-semibold truncate">{signer.name}</p>
                            <p className="text-xs text-gray-500 truncate">{signer.email}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2 shrink-0">
                            <div className="scale-75 md:scale-90">
                              {signer.has_sign ? (
                                <Chip color={'#30C668'}>
                                  {t(translations.COMMON.SIGNED)}
                                </Chip>
                              ) : (
                                <Chip color={'#F02F60'}>
                                  {t(translations.COMMON.NO_SIGN)}
                                </Chip>
                              )}
                            </div>
                            {hasPermissions &&
                              !isObserver &&
                              signer?.status !== IInviteStatus.COMPLETED && (
                                <Button
                                  className="px-3"
                                  isLoading={
                                    isReminderLoading &&
                                    userReminderLoading === signer.idInvitation
                                  }
                                  onClick={() => handleSendReminder(signer.idInvitation)}
                                  size="xs"
                                  color={primaryColor}
                                  variant="solid">
                                  {t(translations.COMMON.SEND_REMINDER)}
                                </Button>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="ml-auto flex h-[20px]">
                        {actionsEnabled && !signer.has_sign && (
                          <MoreButton
                            className="relative top-2 ml-auto"
                            items={
                              links(canDelete, canModify) as Array<{
                                value: string;
                                label: string;
                              }>
                            }
                            onClick={(value) =>
                              onClickMore(value, signer.idInvitation)
                            }
                          />
                        )}
                      </div>
                    </div>

                    {index !== stage.length - 1 && (
                      <hr className="my-4 border border-dashed border-gray-100 md:min-w-full" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <EditSignerModal
        color={primaryColor}
        setShowModal={handleShow}
        isOpen={modal}
        onSuccess={handleEditSuccess}
        signer={editSigner}
        setSigner={cleanSigner}
      />
    </div>
  );
};
